import { useStore } from "../contexts/StoreContext";
import { useState, useEffect, useRef, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";

import { fetchPages, fetchForms } from "../api/queries";
import { updateStore, updatePageSettings } from "../api/mutations";
import { removeCustomDomain } from "../api/queries";
import api from "../api/axios";
import Button from "../components/Button";
import Header from "../components/Header";
import {
  GlobeAltIcon,
  Cog6ToothIcon,
  EyeIcon,
  LinkIcon,
  PlusIcon,
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
} from "@heroicons/react/24/outline";

export default function Club() {
  const { selectedStore } = useStore();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [clubUrl, setClubUrl] = useState("");
  const [whiteLabel, setWhiteLabel] = useState({
    customDomain: "",
    hideFooter: false,
  });
  const [defaultFormId, setDefaultFormId] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [viewMode, setViewMode] = useState("desktop");
  const [previewContainerStyle, setPreviewContainerStyle] = useState({});
  const [iframeStyle, setIframeStyle] = useState({});
  const [showCnameModal, setShowCnameModal] = useState(false);
  const [isCheckingDns, setIsCheckingDns] = useState(false);
  const [dnsStatus, setDnsStatus] = useState(null); // 'pending', 'verified', 'failed'
  const [dnsError, setDnsError] = useState(null);
  const previewPanelRef = useRef(null);
  const iframeRef = useRef(null);

  const { data: pages, isLoading } = useQuery({
    queryKey: ["pages", selectedStore?.id],
    queryFn: () => fetchPages(selectedStore?.id),
    enabled: !!selectedStore?.id,
  });

  const { data: forms } = useQuery({
    queryKey: ["forms", selectedStore?.id],
    queryFn: () => fetchForms(selectedStore?.id),
    enabled: !!selectedStore?.id,
  });

  const clubPage = pages?.find((page) => page.name === "Loyaltify Club");

  // Set up iframe preview URL and white label settings
  useEffect(() => {
    if (selectedStore?.uuid && clubPage?.data) {
      const baseUrl = `${import.meta.env.VITE_PUBLIC_FORM_URL}/${selectedStore.uuid}`;
      const dataHash = encodeURIComponent(JSON.stringify(clubPage.data));
      setPreviewUrl(`${baseUrl}#${dataHash}`);

      // Update white label settings and club URL from club page settings
      setWhiteLabel({
        customDomain: clubPage.settings?.customDomain || "",
        hideFooter: clubPage.settings?.hideFooter || false,
      });

      // Set default form from settings or first form
      const savedDefaultFormId = clubPage.settings?.defaultFormId;
      if (savedDefaultFormId) {
        setDefaultFormId(savedDefaultFormId);
      } else if (forms && forms.length > 0) {
        const firstFormUuid = forms[0].uuid;
        setDefaultFormId(firstFormUuid);

        // Auto-save default form if not set
        const pageUpdateData = {
          storeId: selectedStore.id,
          pageId: clubPage.id,
          settings: {
            ...clubPage.settings,
            defaultFormId: firstFormUuid,
          },
        };

        // Save without waiting for user interaction
        updatePageMutation.mutateAsync(pageUpdateData).catch((error) => {
          console.error("Error auto-saving default form:", error);
        });
      }

      // Set DNS status from database
      console.log("Club page settings:", clubPage.settings);
      console.log("dnsVerified:", clubPage.settings?.dnsVerified);
      console.log("customDomain:", clubPage.settings?.customDomain);

      if (clubPage.settings?.dnsVerified === true) {
        setDnsStatus("verified");
      } else if (
        clubPage.settings?.customDomain &&
        clubPage.settings?.dnsVerified === false
      ) {
        setDnsStatus("failed");
      } else {
        // If dnsVerified is undefined/null or customDomain is not present, reset status
        setDnsStatus(null);
      }

      // Set club URL from page settings or use default
      // If custom domain is verified, use it; otherwise use clubUrl or default
      const customDomain = clubPage.settings?.customDomain;
      const isCustomDomainVerified = clubPage.settings?.dnsVerified === true;
      const savedClubUrl = clubPage.settings?.clubUrl;

      console.log("Club URL logic:", {
        customDomain,
        isCustomDomainVerified,
        savedClubUrl,
        defaultUrl: `${import.meta.env.VITE_PUBLIC_FORM_URL}/${selectedStore.uuid}`,
      });

      if (customDomain && isCustomDomainVerified) {
        setClubUrl(`https://${customDomain}`);
      } else {
        setClubUrl(
          savedClubUrl ||
            `${import.meta.env.VITE_PUBLIC_FORM_URL}/${selectedStore.uuid}`,
        );
      }
    } else if (selectedStore?.uuid) {
      setPreviewUrl(
        `${import.meta.env.VITE_PUBLIC_FORM_URL}/${selectedStore.uuid}`,
      );

      // Set default club URL
      setClubUrl(
        `${import.meta.env.VITE_PUBLIC_FORM_URL}/${selectedStore.uuid}`,
      );
    }
  }, [selectedStore, clubPage]);

  const getPreviewContainerStyle = useCallback(() => {
    if (!previewPanelRef.current) return {};

    const parentContainer = previewPanelRef.current.parentElement;
    if (!parentContainer) return {};

    const availableWidth = parentContainer.clientWidth - 48;
    const availableHeight = parentContainer.clientHeight - 48;

    return {
      width: `${availableWidth}px`,
      height: `${availableHeight}px`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      position: "relative",
    };
  }, []);

  const getIframeStyle = useCallback(() => {
    if (!previewPanelRef.current) return {};

    const parentContainer = previewPanelRef.current.parentElement;
    if (!parentContainer) return {};

    const availableWidth = parentContainer.clientWidth - 48;
    const availableHeight = parentContainer.clientHeight - 48;

    const baseStyle = {
      overflow: "hidden",
      flexShrink: 0,
    };

    if (viewMode === "mobile") {
      const mobileWidth = 320;
      const mobileHeight = 668;
      const borderSize = 48; // Extra space for phone frame
      const scale = Math.min(
        availableWidth / (mobileWidth + borderSize),
        availableHeight / (mobileHeight + borderSize),
      );

      return {
        ...baseStyle,
        width: "320px",
        height: "668px",
        transform: `scale(${scale})`,
        transformOrigin: "center center",
        border: "3px solid #1f2937",
        borderRadius: "48px",
        boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
      };
    }

    const desktopWidth = 1366;
    const desktopHeight = 768;
    const borderSize = 2;
    const scale = Math.min(
      availableWidth / (desktopWidth + borderSize),
      availableHeight / (desktopHeight + borderSize),
    );

    return {
      ...baseStyle,
      width: "1366px",
      height: "768px",
      transform: `scale(${scale})`,
      transformOrigin: "center center",
      borderRadius: "12px",
      boxShadow: "0 20px 60px rgba(0, 0, 0, 0.15)",
    };
  }, [viewMode]);

  // Handle responsive iframe scaling
  useEffect(() => {
    const handleResize = () => {
      if (!previewPanelRef.current) return;
      setPreviewContainerStyle(getPreviewContainerStyle());
      setIframeStyle(getIframeStyle());
    };

    // Debounced resize handler
    let resizeTimeout;
    const debouncedHandleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(handleResize, 250);
    };

    window.addEventListener("resize", debouncedHandleResize);

    // Initial resize
    const timeoutId = setTimeout(() => {
      if (previewPanelRef.current) {
        handleResize();
      }
    }, 100);

    return () => {
      window.removeEventListener("resize", debouncedHandleResize);
      clearTimeout(resizeTimeout);
      clearTimeout(timeoutId);
    };
  }, [viewMode, clubPage, getPreviewContainerStyle, getIframeStyle]);

  const updateStoreMutation = useMutation({
    mutationFn: updateStore,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["stores"] });
      setIsSaving(false);
    },
    onError: (error) => {
      console.error("Error updating store settings:", error);
      setIsSaving(false);
    },
  });

  const updatePageMutation = useMutation({
    mutationFn: updatePageSettings,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["pages", selectedStore?.id],
      });
      setIsSaving(false);
    },
    onError: (error) => {
      console.error("Error updating page settings:", error);
      setIsSaving(false);
    },
  });

  const removeCustomDomainMutation = useMutation({
    mutationFn: (storeId) => removeCustomDomain(storeId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["pages", selectedStore?.id],
      });
      // Reset custom domain in state
      setWhiteLabel((prev) => ({
        ...prev,
        customDomain: "",
      }));
      setDnsStatus(null);
      setDnsError(null);

      // Reset club URL to default
      if (selectedStore?.uuid) {
        setClubUrl(
          `${import.meta.env.VITE_PUBLIC_FORM_URL}/${selectedStore.uuid}`,
        );
      }
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  const handleCreateClub = () => {
    navigate("/club/new");
  };

  const handleEditClub = () => {
    navigate("/club/new");
  };

  const handlePreviewClub = () => {
    if (clubUrl) {
      window.open(clubUrl, "_blank");
    }
  };

  const handleDnsCheck = async () => {
    if (!whiteLabel.customDomain) {
      setDnsError("Please enter a custom domain first");
      return;
    }

    setIsCheckingDns(true);
    setDnsError(null);
    setDnsStatus("pending");

    try {
      // DNS kontrolü için backend API çağrısı
      // Use environment variable for staging, fallback to default
      const expectedCname =
        import.meta.env.VITE_CLOUDFLARE_CUSTOM_ORIGIN_SERVER ||
        "custom.loyaltify.io";

      const response = await api.post(`/stores/${selectedStore.id}/dns_check`, {
        domain: whiteLabel.customDomain,
        expected_cname: expectedCname,
      });

      const result = response.data;

      if (result.success) {
        setDnsStatus("verified");
        setDnsError(null);

        // Update club URL to use custom domain
        if (whiteLabel.customDomain) {
          setClubUrl(`https://${whiteLabel.customDomain}`);
        }

        // DNS doğrulandıktan sonra settings'e kaydet
        if (clubPage) {
          const pageUpdateData = {
            storeId: selectedStore.id,
            pageId: clubPage.id,
            settings: {
              ...clubPage.settings,
              customDomain: whiteLabel.customDomain,
              hideFooter: whiteLabel.hideFooter,
              dnsVerified: true,
              dnsVerifiedAt: new Date().toISOString(),
            },
          };

          await updatePageMutation.mutateAsync(pageUpdateData);
        }

        setShowCnameModal(false);
      } else {
        setDnsStatus("failed");
        setDnsError(result.error || "DNS configuration not found");
      }
    } catch (error) {
      console.error("DNS check failed:", error);
      setDnsStatus("failed");
      setDnsError("DNS check failed. Please try again later.");
    } finally {
      setIsCheckingDns(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!selectedStore?.id) return;

    // If not advanced plan, show CNAME modal
    if (selectedStore?.current_plan !== "advanced") {
      setShowCnameModal(true);
      return;
    }

    setIsSaving(true);

    // Update page with white label settings and club URL if club page exists
    if (clubPage) {
      const pageUpdateData = {
        storeId: selectedStore.id,
        pageId: clubPage.id,
        settings: {
          ...clubPage.settings,
          clubUrl: clubUrl,
          customDomain: whiteLabel.customDomain,
          hideFooter: whiteLabel.hideFooter,
        },
      };

      // Update page with all settings
      try {
        await updatePageMutation.mutateAsync(pageUpdateData);
        // Show CNAME modal after successful save for advanced users
        setShowCnameModal(true);
      } catch (error) {
        console.error("Error updating page settings:", error);
        setIsSaving(false);
      }
    } else {
      // If no club page exists, we can't save settings
      console.error("No club page found to update");
      setIsSaving(false);
    }
  };

  const handleRemoveCustomDomain = async () => {
    if (!selectedStore?.id) return;

    if (
      window.confirm(
        "Are you sure you want to remove the custom domain? This action cannot be undone.",
      )
    ) {
      try {
        await removeCustomDomainMutation.mutateAsync(selectedStore.id);
      } catch (error) {
        console.error("Error removing custom domain:", error);
      }
    }
  };

  return (
    <div className="max-w-8xl mx-auto space-y-6 lg:space-y-8">
      <Header
        icon={GlobeAltIcon}
        title={t("club.title")}
        description={t("club.description")}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
        {/* Settings Panel - Takes 1 column on the left */}
        <div className="space-y-6" id="settings-panel">
          {/* Club Page Status */}
          <div className="bg-gradient-to-br from-white to-gray-50 shadow-lg rounded-xl overflow-hidden border border-gray-100">
            <div className="px-6 py-6">
              {/* Status Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="relative">
                    <div
                      className={`w-3 h-3 rounded-full ${
                        clubPage ? "bg-emerald-500" : "bg-gray-400"
                      }`}
                    ></div>
                    {clubPage && (
                      <div className="absolute inset-0 w-3 h-3 rounded-full bg-emerald-500 animate-ping opacity-75"></div>
                    )}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {clubPage
                        ? t("club.status.online")
                        : t("club.status.offline")}
                    </h3>
                  </div>
                </div>
                {clubPage && (
                  <div className="flex items-center space-x-1 bg-emerald-50 px-3 py-1.5 rounded-full">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                    <span className="text-xs font-medium text-emerald-700">
                      Live
                    </span>
                  </div>
                )}
              </div>

              {/* URL Display */}
              {clubPage && clubUrl && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Club URL
                  </label>
                  <div className="flex items-center space-x-2 bg-gray-50 rounded-lg px-3 py-2 border border-gray-200">
                    <LinkIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span className="text-sm text-gray-600 truncate flex-1">
                      {clubUrl}
                    </span>
                    <button
                      onClick={() => navigator.clipboard.writeText(clubUrl)}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                      title="Copy URL"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              )}

              {/* Default Form Selection */}
              {forms && forms.length > 0 && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Default Form
                  </label>
                  <select
                    value={defaultFormId}
                    onChange={async (e) => {
                      const newFormId = e.target.value;
                      setDefaultFormId(newFormId);

                      // Auto-save default form selection
                      if (clubPage) {
                        try {
                          const pageUpdateData = {
                            storeId: selectedStore.id,
                            pageId: clubPage.id,
                            settings: {
                              ...clubPage.settings,
                              defaultFormId: newFormId,
                            },
                          };
                          await updatePageMutation.mutateAsync(pageUpdateData);
                        } catch (error) {
                          console.error("Error updating default form:", error);
                        }
                      }
                    }}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  >
                    {forms.map((form) => (
                      <option key={form.id} value={form.uuid}>
                        {form.pass?.name || `Form ${form.id}`}
                      </option>
                    ))}
                  </select>
                  <p className="mt-2 text-sm text-gray-500">
                    Select the default form that will be shown on your club
                    page.
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="space-y-3">
                {clubPage ? (
                  <div className="grid grid-cols-2 gap-3">
                    <Button onClick={handleEditClub} icon={Cog6ToothIcon}>
                      {t("club.actions.edit")}
                    </Button>
                    {clubUrl && (
                      <Button
                        variant="secondary"
                        onClick={handlePreviewClub}
                        icon={EyeIcon}
                      >
                        {t("club.actions.preview")}
                      </Button>
                    )}
                  </div>
                ) : (
                  <Button
                    onClick={handleCreateClub}
                    icon={PlusIcon}
                    className="w-full"
                  >
                    {t("club.preview.createButton")}
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* White Label Configuration */}
          <div
            className={`shadow-sm rounded-lg overflow-hidden ${
              selectedStore?.current_plan !== "advanced"
                ? "bg-gray-50 opacity-60"
                : "bg-white"
            }`}
          >
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-medium text-gray-900">
                  {t("club.whiteLabel.title")}
                </h2>
                {selectedStore?.current_plan !== "advanced" && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    {t("club.whiteLabel.advancedPlanRequired")}
                  </span>
                )}
              </div>
              <div className="space-y-6">
                {/* Custom Domain */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("club.whiteLabel.customDomain.label")}
                  </label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <input
                        type="text"
                        value={whiteLabel.customDomain}
                        onChange={(e) => {
                          setWhiteLabel((prev) => ({
                            ...prev,
                            customDomain: e.target.value,
                          }));
                          // Reset DNS status when domain changes
                          setDnsStatus(null);
                          setDnsError(null);
                        }}
                        placeholder={t(
                          "club.whiteLabel.customDomain.placeholder",
                        )}
                        disabled={selectedStore?.current_plan !== "advanced"}
                        className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm ${
                          selectedStore?.current_plan !== "advanced"
                            ? "bg-gray-100 cursor-not-allowed"
                            : ""
                        }`}
                      />
                      {/* DNS Status Indicator */}
                      {whiteLabel.customDomain && (
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                          {dnsStatus === "verified" && (
                            <div className="flex items-center text-green-600">
                              <svg
                                className="w-4 h-4"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </div>
                          )}
                          {dnsStatus === "failed" && (
                            <div className="flex items-center text-red-600">
                              <svg
                                className="w-4 h-4"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </div>
                          )}
                          {dnsStatus === "pending" && (
                            <div className="flex items-center text-yellow-600">
                              <svg
                                className="w-4 h-4 animate-spin"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                              </svg>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Remove Custom Domain Button */}
                    {whiteLabel.customDomain && dnsStatus === "verified" && (
                      <button
                        type="button"
                        onClick={handleRemoveCustomDomain}
                        disabled={removeCustomDomainMutation.isPending}
                        className="inline-flex items-center px-3 py-2 border border-red-300 shadow-sm text-sm leading-4 font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {removeCustomDomainMutation.isPending ? (
                          <>
                            <svg
                              className="animate-spin -ml-1 mr-2 h-4 w-4 text-red-700"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            Removing...
                          </>
                        ) : (
                          "Remove"
                        )}
                      </button>
                    )}
                  </div>

                  {/* DNS Status Messages */}
                  {dnsError && (
                    <p className="mt-2 text-sm text-red-600">{dnsError}</p>
                  )}
                  {dnsStatus === "verified" && (
                    <p className="mt-2 text-sm text-green-600">
                      ✓ DNS configuration verified
                    </p>
                  )}

                  <p className="mt-2 text-sm text-gray-600">
                    {t("club.whiteLabel.customDomain.description")}
                  </p>
                  <p className="mt-2 text-sm text-gray-500">
                    {t("club.whiteLabel.customDomain.helpText")}{" "}
                    <a
                      href="https://loyaltify.io/demo"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-indigo-600 hover:text-indigo-500 underline"
                    >
                      {t("club.whiteLabel.customDomain.bookConsultation")}
                    </a>
                  </p>
                </div>

                {/* Hide Footer */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="hideFooter"
                    checked={whiteLabel.hideFooter}
                    onChange={(e) =>
                      setWhiteLabel((prev) => ({
                        ...prev,
                        hideFooter: e.target.checked,
                      }))
                    }
                    disabled={selectedStore?.current_plan !== "advanced"}
                    className={`h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded ${
                      selectedStore?.current_plan !== "advanced"
                        ? "cursor-not-allowed opacity-50"
                        : ""
                    }`}
                  />
                  <label
                    htmlFor="hideFooter"
                    className={`ml-3 block text-sm font-medium ${
                      selectedStore?.current_plan !== "advanced"
                        ? "text-gray-400"
                        : "text-gray-700"
                    }`}
                  >
                    {t("club.whiteLabel.hideFooter.label")}
                  </label>
                </div>

                {/* Save Settings Button */}
                <div className="pt-4 border-t border-gray-200">
                  <Button
                    onClick={handleSaveSettings}
                    disabled={
                      isSaving ||
                      !selectedStore?.id ||
                      !clubPage ||
                      !whiteLabel.customDomain.trim()
                    }
                    className="w-full"
                  >
                    {isSaving
                      ? t("club.actions.saving")
                      : t("club.actions.save")}
                  </Button>
                  {selectedStore?.current_plan !== "advanced" && (
                    <p className="mt-2 text-sm text-gray-500 text-center">
                      {t("club.whiteLabel.advancedPlanRequired")}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Club Page Preview - Takes 2 columns on the right */}
        <div className="lg:col-span-2 bg-white shadow-sm rounded-lg overflow-hidden">
          <div className="px-4 py-4 sm:px-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-gray-900">
                {t("club.preview.title")}
              </h2>
              <div
                className="inline-flex items-center bg-gray-100 rounded-lg p-1"
                role="group"
              >
                <button
                  onClick={() => setViewMode("desktop")}
                  className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    viewMode === "desktop"
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                  title="Desktop View"
                >
                  <ComputerDesktopIcon className="h-4 w-4 mr-1.5" />
                  Desktop
                </button>
                <button
                  onClick={() => setViewMode("mobile")}
                  className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    viewMode === "mobile"
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                  title="Mobile View"
                >
                  <DevicePhoneMobileIcon className="h-4 w-4 mr-1.5" />
                  Mobile
                </button>
              </div>
            </div>

            <div
              className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 flex items-center justify-center overflow-hidden"
              style={{ minHeight: "calc(100vh - 280px)" }}
            >
              {previewUrl && clubPage ? (
                <div ref={previewPanelRef} style={previewContainerStyle}>
                  <div style={{ position: "relative", ...iframeStyle }}>
                    {viewMode === "mobile" ? (
                      <>
                        {/* Dynamic Island */}
                        <div
                          style={{
                            position: "absolute",
                            top: "16px",
                            left: "50%",
                            transform: "translateX(-50%)",
                            width: "80px",
                            height: "26px",
                            background: "#000",
                            borderRadius: "24px",
                            zIndex: 20,
                          }}
                        ></div>

                        {/* Screen container */}
                        <div
                          style={{
                            width: "100%",
                            height: "100%",
                            background: "white",
                            borderRadius: "40px",
                            overflow: "hidden",
                            position: "relative",
                          }}
                        >
                          {/* Status Bar */}
                          <div
                            style={{
                              position: "absolute",
                              top: "20px",
                              left: "32px",
                              right: "32px",
                              zIndex: 20,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              fontSize: "10px",
                              fontWeight: "700",
                              color: "#000",
                            }}
                          >
                            <span>
                              {new Date().toLocaleTimeString("en-US", {
                                hour: "numeric",
                                minute: "2-digit",
                                hour12: true,
                              })}
                            </span>
                          </div>

                          {/* Iframe */}
                          <iframe
                            ref={iframeRef}
                            src={previewUrl}
                            style={{
                              width: "100%",
                              height: "100%",
                              border: "none",
                              display: "block",
                              paddingTop: "48px",
                            }}
                            onLoad={(e) => {
                              try {
                                const iframeDoc =
                                  e.target.contentDocument ||
                                  e.target.contentWindow.document;
                                const style = iframeDoc.createElement("style");
                                style.textContent = `
                                  * { scrollbar-width: none !important; -ms-overflow-style: none !important; }
                                  *::-webkit-scrollbar { display: none !important; width: 0 !important; height: 0 !important; }
                                  html, body { scrollbar-width: none !important; -ms-overflow-style: none !important; overflow-x: hidden !important; }
                                `;
                                iframeDoc.head.appendChild(style);
                              } catch (e) {
                                // Cross-origin iframe, can't access
                              }
                            }}
                            title="Club Page Preview"
                          />
                        </div>

                        {/* Home Indicator */}
                        <div
                          style={{
                            position: "absolute",
                            bottom: "12px",
                            left: "50%",
                            transform: "translateX(-50%)",
                            width: "96px",
                            height: "4px",
                            background: "#1f2937",
                            borderRadius: "100px",
                            zIndex: 20,
                          }}
                        ></div>
                      </>
                    ) : (
                      <iframe
                        ref={iframeRef}
                        src={previewUrl}
                        style={{
                          width: "100%",
                          height: "100%",
                          border: "none",
                          display: "block",
                        }}
                        onLoad={(e) => {
                          try {
                            const iframeDoc =
                              e.target.contentDocument ||
                              e.target.contentWindow.document;
                            const style = iframeDoc.createElement("style");
                            style.textContent = `
                              * { scrollbar-width: none !important; -ms-overflow-style: none !important; }
                              *::-webkit-scrollbar { display: none !important; width: 0 !important; height: 0 !important; }
                              html, body { scrollbar-width: none !important; -ms-overflow-style: none !important; }
                            `;
                            iframeDoc.head.appendChild(style);
                          } catch (e) {
                            // Cross-origin iframe, can't access
                          }
                        }}
                        title="Club Page Preview"
                      />
                    )}
                  </div>
                </div>
              ) : clubPage ? (
                <div className="text-center py-12">
                  <EyeIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {t("club.preview.loading")}
                  </h3>
                  <p className="text-gray-600">
                    {t("club.preview.loadingDescription")}
                  </p>
                </div>
              ) : (
                <div className="text-center py-12">
                  <EyeIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {t("club.preview.noPage")}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {t("club.preview.noPageDescription")}
                  </p>
                  <Button onClick={handleCreateClub} icon={PlusIcon}>
                    {t("club.preview.createButton")}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* CNAME Modal */}
      {showCnameModal && (
        <div className="fixed inset-0 bg-gray-600/50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-center w-12 h-12 mx-auto bg-blue-100 rounded-full">
                <svg
                  className="w-6 h-6 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div className="mt-2 px-7 py-3">
                <h3 className="text-lg font-medium text-gray-900 text-center">
                  Custom Domain Setup Required
                </h3>
                <div className="mt-2 px-1 py-3">
                  <p className="text-sm text-gray-500 text-center mb-4">
                    To use white label features, you need to add the following
                    CNAME record to your domain's DNS settings:
                  </p>

                  <div className="bg-gray-50 p-4 rounded-lg mb-4">
                    <div className="text-sm">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-medium">Type:</span>
                        <span className="bg-white px-2 py-1 rounded border text-xs">
                          CNAME
                        </span>
                      </div>
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-medium">Name:</span>
                        <span className="bg-white px-2 py-1 rounded border text-xs font-mono">
                          {whiteLabel.customDomain || "your-domain.com"}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="font-medium">Value:</span>
                        <span className="bg-white px-2 py-1 rounded border text-xs font-mono">
                          {import.meta.env
                            .VITE_CLOUDFLARE_CUSTOM_ORIGIN_SERVER ||
                            "custom.loyaltify.io"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <p className="text-xs text-gray-500 text-center">
                    After adding the CNAME record, it may take up to 24 hours
                    for the changes to take effect.
                  </p>

                  {/* DNS Status in Modal */}
                  {dnsStatus === "pending" && (
                    <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                      <div className="flex items-center">
                        <svg
                          className="w-4 h-4 text-yellow-600 animate-spin mr-2"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        <span className="text-sm text-yellow-800">
                          Checking DNS configuration...
                        </span>
                      </div>
                    </div>
                  )}

                  {dnsStatus === "verified" && (
                    <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md">
                      <div className="flex items-center">
                        <svg
                          className="w-4 h-4 text-green-600 mr-2"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span className="text-sm text-green-800">
                          DNS configuration verified successfully!
                        </span>
                      </div>
                    </div>
                  )}

                  {dnsStatus === "failed" && dnsError && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                      <div className="flex items-start">
                        <svg
                          className="w-4 h-4 text-red-600 mr-2 mt-0.5 flex-shrink-0"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <div>
                          <p className="text-sm text-red-800 font-medium">
                            DNS configuration failed
                          </p>
                          <p className="text-sm text-red-700 mt-1">
                            {dnsError}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="items-center px-4 py-3">
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={handleDnsCheck}
                      disabled={isCheckingDns}
                      className="px-6 py-2 bg-indigo-600 text-white text-base font-medium rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isCheckingDns ? "Checking..." : "I configured"}
                    </button>
                    <button
                      onClick={() => setShowCnameModal(false)}
                      className="px-6 py-2 bg-white text-gray-500 text-base font-medium rounded-md border border-gray-300 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
